// src/components/MicButton.js — v7.1 (Mute/Unmute + English transcription safety net)
//
// CHANGES FROM v7:
//   1. classifyIntent() now defaults to 'sales' instead of 'casual' for
//      unrecognized input. Since the gateway now translates all speech to
//      English, keywords should match. But as a safety net, anything that
//      isn't clearly a greeting goes to the backend (which handles errors
//      gracefully) rather than Gemini (which would fabricate data).
//
// PRESERVED FROM v7:
//   1. MIC MUTE/UNMUTE: During processing & speaking phases, the mic button
//      becomes a mute toggle instead of being disabled. Users can mute to have
//      a side conversation, then unmute when ready.
//   2. Button is NEVER disabled during an active voice session (except initial
//      connection). It always does something useful:
//      - idle → start listening
//      - listening → stop voice mode
//      - processing/speaking → toggle mute/unmute
//   3. Visual states: idle | listening | processing | playing | muted
//      The 'muted' state shows a muted-mic icon with a strike-through.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { voiceGatewayService } from '../services/voiceGatewayService';
import config from '../config';
import '../styles/MicButton.css';

// ── Acknowledgment messages (spoken immediately after user question) ──
const ACK_MESSAGES = [
  'OK, let me analyze your data. One moment please.',
  'Sure, I am fetching the data for you. Please wait.',
  'Got it. Let me pull up that information for you.',
  'Alright, analyzing the sales data now. Just a moment.',
  'OK, checking the database for you. Hold on.',
];

// ── Staged followup messages for long-running Cortex queries ──
const FOLLOWUP_STAGES = [
  {
    delayMs: 10000,
    messages: [
      'I am still processing your request. The AI agent is analyzing your data.',
      'Still working on it. The system is querying the database.',
      'Your question is being analyzed. One more moment please.',
    ]
  },
  {
    delayMs: 25000,
    messages: [
      'This is a complex query. The AI is still crunching the numbers.',
      'Still processing. Complex data analysis takes a bit longer.',
      'Almost there. The system is running a detailed analysis.',
    ]
  },
  {
    delayMs: 45000,
    messages: [
      'I appreciate your patience. The AI is working through a large dataset.',
      'Still working. This query involves multiple data sources.',
      'Hang tight. The analysis is in progress and should be ready soon.',
    ]
  },
  {
    delayMs: 70000,
    messages: [
      'This is taking longer than usual. The data volume is large but I will have the answer shortly.',
      'Almost done. The system is finalizing the results.',
      'Thank you for waiting. The AI agent is completing the analysis.',
    ]
  },
  {
    delayMs: 100000,
    messages: [
      'Still processing. Complex analytics queries can take up to two minutes. Please bear with me.',
      'The analysis is nearly complete. Just a few more moments.',
    ]
  },
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ════════════════════════════════════════════════════════════════
// INTENT DETECTION (v7.1 — default to 'sales' for safety)
// ════════════════════════════════════════════════════════════════

const SALES_KEYWORDS = [
  // ── Core sales terms ──
  'sale', 'sales', 'sell', 'sold', 'revenue', 'volume', 'amount', 'total',
  'discount', 'growth', 'target', 'achievement', 'performance', 'trend',
  'average', 'sum', 'count', 'top', 'highest', 'lowest', 'best', 'worst',
  'maximum', 'minimum', 'compare', 'comparison', 'versus', 'vs',
  'percentage', 'percent', 'ratio', 'margin', 'profit',
  'bottom', 'rank', 'share', 'split', 'breakup', 'wise',
  'change', 'difference', 'compared to',

  // ── Time periods ──
  'quarter', 'month', 'year', 'week', 'daily', 'monthly', 'quarterly',
  'yearly', 'annual', 'ytd', 'mtd', 'q1', 'q2', 'q3', 'q4',
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
  'last month', 'this month', 'last year', 'this year', 'last quarter',
  'today', 'yesterday', 'this week', 'last week', 'this quarter',
  'last 7 days', 'last 30 days', 'last 60 days', 'last 90 days',
  'custom date', 'date range',
  'yoy', 'qoq', 'mom', 'year on year', 'month on month', 'month over month',

  // ── Q1–Q6: Revenue & Volume Performance ──
  'secondary sales', 'secondary revenue', 'total revenue secondary', 'secondary billing',
  'secondary amount', 'month revenue', 'quarter revenue', 'yearly revenue',
  'this month sales', 'this quarter sales', 'mtd secondary', 'qtd secondary', 'ytd secondary',
  'primary sales', 'primary revenue', 'total revenue primary', 'primary billing',
  'primary amount', 'company sales', 'dispatch value', 'mtd primary', 'qtd primary', 'ytd primary',
  'volume sold', 'total volume', 'volume by category', 'volume by brand',
  'category volume', 'brand volume', 'units sold', 'quantity sold',
  'category wise volume', 'brand wise volume',
  'mom growth', 'revenue growth', 'growth trend', 'monthly trend',
  'revenue trend', 'sales trend', 'monthly growth',
  'offtake ratio', 'primary to secondary', 'primary secondary ratio', 'offtake',
  'sell-through ratio', 'pipeline ratio', 'dispatch vs offtake', 'p2s ratio',
  'mrp realization', 'average realization', 'realization per unit', 'effective mrp',
  'price realization', 'per unit realization', 'avg realization',
  'primary', 'secondary', 'dispatch', 'billing',

  // ── Q7–Q11: Distributor Performance ──
  'distributor', 'distributors', 'customer', 'customers',
  'top distributors', 'best distributors', 'top 10 distributors', 'highest selling distributors',
  'distributor ranking', 'distributor leaderboard', 'best performing distributors',
  'declining distributors', 'distributor decline', 'distributors falling',
  'sales drop distributor', 'underperforming distributors', 'distributor degrowth',
  'negative growth distributor', 'declining sales distributor',
  'stock lying', 'distributor stock', 'pipeline stock', 'inventory with distributor',
  'stock in hand', 'primary minus secondary', 'unsold stock', 'distributor inventory', 'stock holding',
  'zero sales distributor', 'no sales distributor', 'inactive distributor',
  'non-billing distributor', 'dormant distributor', 'zero secondary',
  'no billing distributor', 'no offtake',
  'average order value', 'aov distributor', 'avg order value', 'per order value',
  'order size distributor', 'mean order value',

  // ── Q12–Q15: Territory & Region Analysis ──
  'territory', 'region', 'city', 'hos', 'zone',
  'territory sales', 'region sales', 'territory wise', 'region wise',
  'territory performance', 'region performance', 'zone wise sales', 'area wise sales',
  'underperforming territory', 'territory decline', 'low performing territory',
  'territory vs target', 'territory vs last year', 'territory degrowth', 'weak territory', 'territory yoy',
  'city wise', 'city sales', 'city contribution', 'city breakup', 'city split',
  'sales by city', 'top cities', 'city performance',
  'coo wise', 'mugm wise', 'hos wise', 'hierarchy sales', 'sales heatmap',
  'coo sales', 'mugm sales', 'hos sales', 'org hierarchy', 'management hierarchy',
  'area', 'plant', 'warehouse', 'depot',

  // ── Q16–Q20: Product & SKU Analysis ──
  'brand', 'brands', 'product', 'products', 'category', 'categories',
  'sku', 'flavor', 'variant',
  'top sku', 'top selling sku', 'best selling product', 'top 10 sku',
  'top products', 'sku ranking', 'highest selling sku', 'best sku', 'top items',
  'category wise sales', 'brand wise sales', 'category split', 'brand split',
  'category breakup', 'brand breakup', 'category contribution', 'brand contribution', 'brand share',
  'zero sales sku', 'dead sku', 'non-moving sku', 'no sales product',
  'inactive sku', 'slow moving sku', 'dormant sku', 'non-selling sku', 'sku no movement',
  'pack wise', 'container wise', 'pack split', 'container split',
  'pack distribution', 'container distribution', 'pack type sales', 'container type', 'packaging wise',
  'flavor wise', 'flavor performance', 'flavor trend', 'flavor sales',
  'flavor split', 'taste wise', 'variant wise sales', 'flavor growth',

  // ── Q21–Q26: Retailer & Channel Analytics ──
  'retailer', 'retailers', 'outlet', 'outlets',
  'active retailers', 'billing outlets', 'active outlets', 'how many retailers',
  'retailer count', 'outlet count', 'active billing', 'eco count', 'effective coverage',
  'channel', 'channel wise', 'sub-channel wise', 'channel breakup', 'channel split',
  'sub channel sales', 'channel performance', 'channel contribution',
  'strike rate', 'billing efficiency', 'retailer productivity', 'outlet billing rate',
  'billing percentage', 'productive calls', 'retailer strike',
  'top retailers', 'best retailers', 'top 20 retailers', 'highest selling retailers',
  'retailer ranking', 'best outlets', 'top outlets',
  'new retailers', 'new outlets', 'retailers added', 'new accounts',
  'outlet addition', 'new opening', 'new onboarding', 'retailer onboarding',
  'lines per call', 'lpc', 'products per bill', 'range selling',
  'product range', 'sku per visit', 'items per call', 'lines per bill', 'average lines',

  // ── Q27–Q31: Salesman Productivity ──
  'salesman', 'salesperson', 'dsr', 'se',
  'salesman sales', 'salesman achievement', 'salesman performance',
  'sales rep performance', 'salesman target', 'dsr performance',
  'salesman wise sales', 'se performance',
  'inactive salesman', 'no bills salesman', 'zero billing salesman',
  'dormant salesman', 'salesman no order', 'salesman not billing',
  'idle salesman', 'non-productive salesman',
  'hht', 'handheld', 'terminal', 'order split',
  'hht adoption', 'hht vs non-hht', 'hht order', 'device order',
  'digital order', 'hht split', 'hht percentage',
  'average bill value', 'bill value salesman', 'avg bill value',
  'per bill value', 'ticket size salesman', 'mean bill value',
  'outlet coverage', 'outlets per salesman', 'unique outlets',
  'coverage per salesman', 'market coverage', 'beat coverage',
  'salesman coverage', 'salesman outlet count',

  // ── Q32–Q35: Discount & Pricing Analysis ──
  'total discount', 'discount this month', 'discount value', 'discount amount',
  'discount percentage', 'monthly discount', 'scheme cost', 'trade spend',
  'discount type', 'discount breakup', 'discount split', 'sub discount type',
  'type wise discount', 'discount category', 'scheme type', 'scheme breakup',
  'highest discount', 'top discount', 'discount by retailer', 'discount by channel',
  'discount outlier', 'maximum discount', 'heavy discount', 'over-discounted',
  'effective price', 'selling price vs mrp', 'mrp vs actual', 'price gap',
  'realization vs mrp', 'effective selling price', 'net selling price',
  'price erosion', 'mrp deviation',

  // ── Q36–Q39: Primary Sales & Supply Chain ──
  'plant wise billing', 'warehouse billing', 'plant wise sales', 'depot wise',
  'plant performance', 'warehouse dispatch', 'plant dispatch', 'depot sales',
  'returns', 'credit note', 'credit memo', 'return value', 'primary returns',
  'return percentage', 'cn value', 'goods return', 'sales return',
  '8oz equivalent', '8 oz', 'eight ounce', 'standard volume',
  'equivalent volume', 'normalized volume', '8oz qty', '8oz conversion',
  'tax breakup', 'tax component', 'excise', 'vat', 'cst', 'cess', 'tcs',
  'tax split', 'tax percentage', 'tax wise', 'tax details',

  // ── Q40–Q42: Key Account & Modern Trade ──
  'key account', 'key account sales', 'key account performance', 'ka sales',
  'ka performance', 'named accounts', 'institutional sales',
  'gt vs mt', 'general trade', 'modern trade', 'gt mt split',
  'trade split', 'gt sales', 'mt sales', 'traditional vs modern', 'trade channel split',
  'key account growth', 'key account decline', 'ka growth', 'ka trend',
  'key account trend', 'key account comparison', 'ka degrowth',

  // ── Q43–Q45: Operational & Exception Alerts ──
  'sync delay', 'data sync', 'sync version', 'sync status', 'data delay',
  'sync check', 'last sync', 'data freshness', 'data update status', 'syncversion',
  'operation mismatch', 'sub-operation mismatch', 'master data mismatch',
  'operation code issue', 'data discrepancy', 'code mismatch', 'master mismatch', 'data quality',
  'flagged transactions', 'transaction flag', 'txn type', 'flagged invoice',
  'audit flag', 'exception transactions', 'returns flag', 'damages flag',
  'sample flag', 'transaction type',
  'invoice',

  // ── VBL Brands ──
  'pepsi', 'sting', 'mountain dew', 'slice', 'aquafina', 'mirinda',
  '7up', 'seven up', 'tropicana', 'gatorade', 'lipton', 'nimbooz',
  'kurkure', 'lays', 'doritos', 'teem', 'lehar', 'dukes',
  'creambell', 'fruitz',

  // ── Packaging ──
  'pet', 'can', 'glass', 'bottle', 'pack', 'container',
  'carbonated', 'juice', 'water', 'energy drink', 'cup',

  // ── VBL specific ──
  'samna', 'non-samna', 'hub', 'stock point',

  // ── Hindi terms (for edge cases where translation may not work perfectly) ──
  'bikri', 'bechna', 'karobar', 'kitna', 'sabse', 'zyada',
  'kam', 'data', 'report',
];

const CASUAL_PATTERNS = [
  /^(hi|hello|hey|good morning|good afternoon|good evening|namaste|namaskar)\b/i,
  /^(thank|thanks|thankyou|dhanyavaad|shukriya)\b/i,
  /^(bye|goodbye|see you|alvida)\b/i,
  /^(how are you|what's up|kaise ho|kya haal)\b/i,
  /^(what can you do|help me|who are you|kya kar sakte)\b/i,
  /^(ok|okay|alright|theek hai|accha)\s*$/i,
  /^(yes|no|haan|nahi|hmm)\s*$/i,
];

/**
 * Classify user intent as 'casual' or 'sales'.
 *
 * v7.1 CHANGE: Default is now 'sales' instead of 'casual'.
 * Rationale: With the gateway translating all speech to English,
 * keyword matching should work reliably. But as a safety net,
 * anything that isn't clearly a greeting goes to the backend.
 * The backend handles errors gracefully (returns error message),
 * while Gemini would fabricate data if given a sales question
 * through the casual/direct path.
 */
function classifyIntent(text) {
  if (!text || text.trim().length < 3) return 'casual';
  const lower = text.toLowerCase().trim();

  // Only clearly casual messages get the casual path
  for (const pattern of CASUAL_PATTERNS) {
    if (pattern.test(lower)) return 'casual';
  }

  // Check for known sales keywords (fast match for obvious cases)
  for (const keyword of SALES_KEYWORDS) {
    if (lower.includes(keyword)) return 'sales';
  }

  // Question patterns → sales
  if (/\?/.test(lower) || /^(what|which|how|when|where|who|show|give|tell|list|find|get|fetch|display)\b/i.test(lower)) {
    return 'sales';
  }

  // Hindi question patterns (for edge cases where translation may keep some Hindi)
  if (/^(kya|kaun|kab|kahan|kitna|kitne|dikha|bata|kaunsa)\b/i.test(lower)) {
    return 'sales';
  }

  // v7.1: DEFAULT TO SALES instead of casual
  // Better to send an unrecognized query to the backend (which returns
  // a graceful error) than to Gemini (which fabricates data).
  return 'sales';
}

// ════════════════════════════════════════════════════════════════
// MIC BUTTON COMPONENT
// ════════════════════════════════════════════════════════════════

const MicButton = ({
  sendQueryFn,
  onVoiceExchange,
  disabled = false,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceState, setVoiceState] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const voiceActiveRef = useRef(false);
  const gatewayInitRef = useRef(false);

  const followupTimersRef = useRef([]);
  const pendingQuestionRef = useRef('');
  const pendingBackendRef = useRef(null);

  useEffect(() => { voiceActiveRef.current = voiceActive; }, [voiceActive]);

  const clearAllFollowupTimers = () => {
    followupTimersRef.current.forEach(id => clearTimeout(id));
    followupTimersRef.current = [];
  };

  const startFollowupTimers = () => {
    clearAllFollowupTimers();
    FOLLOWUP_STAGES.forEach((stage) => {
      const timerId = setTimeout(() => {
        if (!mountedRef.current) return;
        voiceGatewayService.speakFollowup(pickRandom(stage.messages));
      }, stage.delayMs);
      followupTimersRef.current.push(timerId);
    });
  };

  useEffect(() => {
    if (!config.features?.enableVoiceMode) return;
    mountedRef.current = true;
    if (!gatewayInitRef.current) {
      gatewayInitRef.current = true;
      initGateway();
    }
    return () => { mountedRef.current = false; clearAllFollowupTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initGateway = async () => {
    try {
      setError(null);
      const lang = localStorage.getItem(config.storage?.voiceLanguageKey)
        || config.voice?.defaultLanguage || 'hi-IN';

      voiceGatewayService.on('ready', () => {
        if (mountedRef.current) { setIsReady(true); setError(null); }
      });

      // ── v7: Listen for mute state changes from the service ──
      voiceGatewayService.on('mute_changed', ({ muted }) => {
        if (mountedRef.current) setIsMuted(muted);
      });

      // ── User question transcribed ───────────────────────────
      voiceGatewayService.on('user_question', async ({ text }) => {
        if (!mountedRef.current || !text) return;

        console.log('[MicButton] User question:', text);
        const intent = classifyIntent(text);
        console.log('[MicButton] Intent:', intent);

        if (intent === 'casual') {
          if (mountedRef.current) setVoiceState('speaking');
          if (onVoiceExchange) {
            onVoiceExchange({ question: text, phase: 'question_only' });
          }
          voiceGatewayService.speakDirect(
            `The user said: "${text}". Respond naturally and conversationally as a friendly VBL Sales AI assistant. Keep it brief (1-2 sentences). If they seem to be asking about data but you are unsure, suggest they ask a specific sales question.`
          );
          pendingQuestionRef.current = text;
          return;
        }

        // ── SALES INTENT → backend query flow ──
        if (mountedRef.current) setVoiceState('processing');
        pendingQuestionRef.current = text;
        pendingBackendRef.current = null;

        if (onVoiceExchange) {
          onVoiceExchange({ question: text, phase: 'question_only' });
        }

        voiceGatewayService.speakAck(pickRandom(ACK_MESSAGES));

        if (sendQueryFn) {
          startFollowupTimers();

          try {
            const result = await sendQueryFn(text);
            clearAllFollowupTimers();
            if (!mountedRef.current) return;

            if (result?.responseText) {
              pendingBackendRef.current = result.fullResponse || null;
              if (mountedRef.current) setVoiceState('speaking');
              voiceGatewayService.speakData(result.responseText);
            } else {
              pendingQuestionRef.current = '';
              pendingBackendRef.current = null;
              if (mountedRef.current) setVoiceState(voiceActiveRef.current ? 'listening' : 'idle');
            }
          } catch (err) {
            clearAllFollowupTimers();
            console.error('[MicButton] Backend error:', err);
            pendingBackendRef.current = null;

            if (onVoiceExchange) {
              onVoiceExchange({
                question: text,
                answer: `Error: ${err.message}`,
                phase: 'full_response',
              });
            }

            voiceGatewayService.speakDirect(
              `Sorry, there was an error processing your request. Please try again.`
            );
            pendingQuestionRef.current = '';
          }
        }
      });

      // ── Gemini finished speaking data summary ────────
      voiceGatewayService.on('voice_done', ({ summary }) => {
        if (!mountedRef.current) return;
        clearAllFollowupTimers();

        const question = pendingQuestionRef.current;
        const backendResponse = pendingBackendRef.current;
        pendingQuestionRef.current = '';
        pendingBackendRef.current = null;

        console.log('[MicButton] Voice done, summary:', summary?.substring(0, 80));

        if (onVoiceExchange) {
          onVoiceExchange({
            question,
            answer: summary || 'Data processed.',
            backendResponse,
            phase: 'full_response',
          });
        }

        if (mountedRef.current) setVoiceState(voiceActiveRef.current ? 'listening' : 'idle');
      });

      // ── Progressive text display ───
      voiceGatewayService.on('output_transcript_chunk', ({ text, phase }) => {
        if (!mountedRef.current || !text) return;
        if (onVoiceExchange) {
          onVoiceExchange({
            question: pendingQuestionRef.current,
            answer: text,
            phase: 'streaming',
          });
        }
      });

      // ── Direct conversational response finished ──
      voiceGatewayService.on('direct_done', ({ text }) => {
        if (!mountedRef.current) return;

        const question = pendingQuestionRef.current;
        pendingQuestionRef.current = '';
        pendingBackendRef.current = null;

        console.log('[MicButton] Direct done:', text?.substring(0, 80));

        if (onVoiceExchange) {
          onVoiceExchange({
            question,
            answer: text || '',
            phase: 'full_response',
          });
        }

        if (mountedRef.current) setVoiceState(voiceActiveRef.current ? 'listening' : 'idle');
      });

      // ── Session expired ─────────────────────────────
      voiceGatewayService.on('session_expired', () => {
        if (!mountedRef.current) return;
        clearAllFollowupTimers();
        setVoiceActive(false);
        setVoiceState('idle');
        setIsMuted(false);
      });

      voiceGatewayService.on('interrupted', () => {
        clearAllFollowupTimers();
        if (mountedRef.current) setVoiceState(voiceActiveRef.current ? 'listening' : 'idle');
      });

      voiceGatewayService.on('error', (err) => {
        if (mountedRef.current) setError(err.message || 'Voice error');
      });

      voiceGatewayService.on('disconnected', () => {
        clearAllFollowupTimers();
        if (mountedRef.current) {
          setIsReady(false);
          setVoiceActive(false);
          setVoiceState('idle');
          setIsMuted(false);
        }
      });

      await voiceGatewayService.connect({ appId: config.voiceGatewayAppId, targetLanguage: lang });
    } catch (err) {
      if (mountedRef.current) { setIsReady(false); setError(err.message); }
    }
  };

  // ══════════════════════════════════════════════════════════════
  // v7: Updated click handler — context-aware behavior
  // ══════════════════════════════════════════════════════════════
  const toggle = useCallback(async () => {
    const isBusy = voiceState === 'processing' || voiceState === 'speaking';

    // ── During processing/speaking: toggle mute instead of start/stop ──
    if (voiceActive && isBusy) {
      voiceGatewayService.toggleMute();
      return;
    }

    // ── Normal start/stop voice mode ──
    if (voiceActive) {
      clearAllFollowupTimers();
      voiceGatewayService.stopMic();
      setVoiceActive(false);
      setVoiceState('idle');
      setIsMuted(false);
    } else {
      try {
        setError(null);
        await voiceGatewayService.startMic();
        setVoiceActive(true);
        setVoiceState('listening');
        setIsMuted(false);
      } catch (err) {
        setError(err.name === 'NotAllowedError' ? 'Mic access denied' : err.message);
      }
    }
  }, [voiceActive, voiceState]);

  if (!config.features?.enableVoiceMode) return null;

  // ── v7: Button is only disabled before connection, never during session ──
  const isDisabled = disabled || !isReady;
  const isBusy = voiceState === 'processing' || voiceState === 'speaking';

  // Determine visual state
  let state;
  if (isDisabled) {
    state = 'disabled';
  } else if (isMuted) {
    state = 'muted';
  } else if (voiceState === 'processing') {
    state = 'processing';
  } else if (voiceState === 'speaking') {
    state = 'playing';
  } else if (voiceActive) {
    state = 'listening';
  } else {
    state = 'idle';
  }

  // Determine tooltip
  let title;
  if (error) {
    title = `Error: ${error}`;
  } else if (!isReady) {
    title = 'Connecting...';
  } else if (isMuted) {
    title = 'Mic muted — Click to unmute';
  } else if (voiceState === 'processing') {
    title = 'Fetching data... Click to mute mic';
  } else if (voiceState === 'speaking') {
    title = 'AI is speaking... Click to mute mic';
  } else if (voiceActive) {
    title = 'Listening... Click to stop';
  } else {
    title = 'Start voice mode';
  }

  return (
    <button
      type="button"
      className={`vbl-mic-btn vbl-mic-${state}`}
      onClick={toggle}
      disabled={isDisabled}
      title={title}
      aria-label={title}
    >
      {/* ── Muted: mic with strike-through ── */}
      {state === 'muted' ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
          {/* Strike-through line */}
          <line x1="3" y1="3" x2="21" y2="21" strokeWidth="2.5" stroke="currentColor" />
        </svg>
      ) : state === 'processing' ? (
        <span className="vbl-mic-spinner" />
      ) : state === 'listening' ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect className="vbl-wave vbl-wave-1" x="4" y="8" width="2" height="8" rx="1" fill="currentColor" />
          <rect className="vbl-wave vbl-wave-2" x="9" y="5" width="2" height="14" rx="1" fill="currentColor" />
          <rect className="vbl-wave vbl-wave-3" x="14" y="7" width="2" height="10" rx="1" fill="currentColor" />
          <rect className="vbl-wave vbl-wave-4" x="19" y="9" width="2" height="6" rx="1" fill="currentColor" />
        </svg>
      ) : state === 'playing' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      )}
    </button>
  );
};

export default MicButton;
